import { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { useModalDismiss } from './use-modal-dismiss';
import { cn } from '@/lib/utils';
import {
  AdminRequestError,
  getGatekeeperSettings,
  updateGatekeeperSettings,
  type GatekeeperEffective,
  type GatekeeperSettings,
} from './api';

// The audio fork's operator surface (plans/gatekeeper). A copy of the lead's first
// seconds goes to the classifier while the call bridges exactly as it always has.
//
// Two dials, and they are different kinds of thing. The first sets how many calls are
// MEASURED and changes no call. The second is the only control here that does: the share
// of measured calls on which a voicemail verdict the classifier is sure of ENDS the call.
// They are saved separately so each writes its own row to the history -- a change to what
// is measured must never be mistaken, later, for a change to what was cut.
//
// And no more than that. The row carries five other columns, but nothing reads them
// yet (the service runs on its own values), so offering them here would write history
// rows for arms that never took effect — Q5 in the plan. They come back when the core
// signs them onto each stream.
//
// Off is one click and needs no confirmation: it takes effect on the next answered call
// across the fleet, and a brake must never ask twice. Turning it ON asks, because that
// is the direction that starts copying customer audio.

// Sampling is a hash of the call id, not a draw, so a retried leg forks the way it did
// the first time — worth saying here, because the natural reading of "1%" is a dice roll
// and that would bias the sample toward calls that were rendered twice.
// Whether a write turns copying ON from a standstill — the only transition that has to
// ask. Read off the LIVE state, not the switch alone: a row that is `enabled` with a 0%
// share is forking nothing, so 0 → 5 starts it just as much as flipping the switch does.
export function startsForking(
  live: { enabled: boolean; sample_pct: number },
  next: { enabled: boolean; sample_pct: number },
): boolean {
  const forkingNow = live.enabled && live.sample_pct > 0;
  const forkingNext = next.enabled && next.sample_pct > 0;
  return !forkingNow && forkingNext;
}

// Whether a write starts ENDING calls from a standstill -- the other transition that has
// to ask, and for the better reason. Read off the live state, like `startsForking`: what
// matters is whether the fleet was cutting a moment ago, not what the row says.
export function startsCutting(live: { act_voicemail_pct?: number }, next: number): boolean {
  return !((live.act_voicemail_pct ?? 0) > 0) && next > 0;
}

// What the confirm says when copying starts. Starting to copy can also RESUME CUTTING:
// the brake writes `enabled` alone -- it must never 422 against a core that predates the
// cut column -- so a share saved before it was pulled is still on the row, and takes
// effect the moment the fork comes back. A prompt that said "decides nothing" there would
// be asking the operator to agree to the opposite of what is about to happen.
export function forkConfirmMessage(pct: number, cutPct: number): string {
  const copying = `Start copying ${pct}% of answered outbound calls to the classifier?`;

  if (cutPct > 0) {
    return `${copying}\n\nThis also RESUMES ENDING CALLS. A cut share of ${cutPct}% is saved, so from the next answered call the classifier will end ${cutPct}% of the voicemails it is sure of. To measure without cutting, cancel, set that share to 0, then turn this on. Takes effect fleet-wide — no deploy. Turning it off here is instant.`;
  }

  return `${copying}\n\nThe classifier decides nothing: it transcribes and records how fast it could have decided. Calls bridge exactly as they do now, and the copy is one-way. Takes effect on the next answered call — no deploy. Turning it off here is instant.`;
}

const CUT_HELP =
  'What share of copied calls a voicemail verdict may end (0–100). Only a verdict resting on carrier or platform speech — “the person you’re calling”, “an automated voice messaging system” — never “leave a message”. It ends a call under exactly the rule a Twilio machine verdict does today, only about two seconds sooner: never once Twilio has confirmed a human; an SDR who has been shown a GUESSED card is protected only while the late-machine policy in AMD settings is on — with it off, the card is retracted, as it already is when Twilio’s verdict lands. Chosen by a hash of the call id, so the calls left out are a like-for-like comparison. 0 ends nothing.';

const SAMPLE_HELP =
  'What share of answered outbound calls are copied to the classifier (0–100). Chosen by a hash of the call id, so a call that is re-rendered forks the same way it did the first time. 0 forks nothing, whatever the switch says.';

export function GatekeeperSettingsModal({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  onSaved?: (effective: GatekeeperEffective) => void;
}) {
  const dialog = useModalDismiss(onClose);
  const [saved, setSaved] = useState<GatekeeperSettings | null>(null);
  const [effective, setEffective] = useState<GatekeeperEffective | null>(null);
  const [pct, setPct] = useState('');
  const [cut, setCut] = useState('');
  const [status, setStatus] = useState('Loading…');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const apply = useCallback(
    (r: { settings: GatekeeperSettings; effective: GatekeeperEffective }) => {
      setSaved(r.settings);
      setEffective(r.effective);
      setPct(String(r.settings.sample_pct));
      setCut(String(r.settings.act_voicemail_pct ?? 0));
      setStatus('');
    },
    [],
  );

  useEffect(() => {
    let live = true;
    getGatekeeperSettings()
      .then((r) => {
        if (live) apply(r);
      })
      .catch((err: unknown) => {
        if (live) setStatus(err instanceof AdminRequestError ? err.message : 'Failed to load');
      });
    return () => {
      live = false;
    };
  }, [apply]);

  const submit = useCallback(
    (
      patch: { enabled?: boolean; sample_pct?: number; act_voicemail_pct?: number },
      note: string,
    ) => {
      setSaving(true);
      setError(null);
      updateGatekeeperSettings({ ...patch, note })
        .then((r) => {
          apply(r);
          setSaving(false);
          onSaved?.(r.effective);
        })
        .catch((err: unknown) => {
          setError(err instanceof AdminRequestError ? err.message : 'Failed to save');
          setSaving(false);
        });
    },
    [apply, onSaved],
  );

  // Parsed once, here: the input stays a string so a half-typed value is not rewritten
  // under the cursor, and everything below reads this.
  const parsed = /^\d+$/.test(pct.trim()) ? Number(pct.trim()) : null;
  const pctValid = parsed !== null && parsed >= 0 && parsed <= 100;
  const pctDirty = saved !== null && pctValid && parsed !== saved.sample_pct;

  // One path for both buttons: whether a write also flips the switch is a detail of the
  // patch, not a different action. What decides the confirm is the transition.
  const save = () => {
    if (!saved || !effective || parsed === null || !pctValid) return;

    if (
      startsForking(effective, { enabled: true, sample_pct: parsed }) &&
      !window.confirm(forkConfirmMessage(parsed, saved.act_voicemail_pct ?? 0))
    ) {
      return;
    }

    // The switch is written only when it is not already on, so a share edit stays a
    // share edit in the history.
    submit(
      saved.enabled ? { sample_pct: parsed } : { enabled: true, sample_pct: parsed },
      saved.enabled ? `fork at ${parsed}%` : `fork on at ${parsed}%`,
    );
  };

  const live = effective?.enabled === true && effective.sample_pct > 0;

  // The cut dial. Offered only when the core says it has the column: one that predates it
  // would 422 the PATCH, and a control that cannot save is worse than none.
  const cutSupported = saved?.act_voicemail_pct !== undefined;
  const cutParsed = /^\d+$/.test(cut.trim()) ? Number(cut.trim()) : null;
  const cutValid = cutParsed !== null && cutParsed >= 0 && cutParsed <= 100;
  const cutDirty = saved !== null && cutValid && cutParsed !== (saved.act_voicemail_pct ?? 0);
  const cutLive = effective?.act_voicemail_pct ?? 0;
  const cutting = live && cutLive > 0;
  // A share on the row while nothing is copied: ending no calls NOW, and armed.
  const cutArmed = !live && (saved?.act_voicemail_pct ?? 0) > 0;

  const saveCut = () => {
    if (!effective || cutParsed === null || !cutValid) return;

    if (
      startsCutting(effective, cutParsed) &&
      !window.confirm(
        `Let the classifier END ${cutParsed}% of the voicemails it is sure of?\n\nThis changes live calls. A call is ended only when the classifier heard carrier or platform speech, and under exactly the rule a Twilio machine verdict ends one today — just about two seconds sooner. It never ends a call once Twilio has confirmed a human. An SDR who has been shown a GUESSED card is protected only while the late-machine policy in AMD settings is on; with it off, that card is retracted, as it already is when Twilio’s verdict lands. Twilio’s own detection keeps running underneath. Takes effect on the next verdict, fleet-wide — no deploy. Setting it back to 0 here is instant.`,
      )
    ) {
      return;
    }

    submit({ act_voicemail_pct: cutParsed }, `cut voicemails at ${cutParsed}%`);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        {...dialog}
        aria-label="Voicemail classifier"
        className="bg-card flex max-h-[90vh] w-[min(32rem,94vw)] flex-col gap-4 overflow-y-auto rounded-lg border p-5 shadow-xl focus:outline-none"
        onClick={(e) => e.stopPropagation()}
      >
        <div>
          <h2 className="text-base font-semibold">
            Voicemail classifier{cutting ? '' : ' (shadow)'}
          </h2>
          <p className="text-muted-foreground text-sm">
            Sends a one-way copy of the lead’s first seconds to the classifier while the call
            bridges as normal.{' '}
            {cutting
              ? `It is ending ${cutLive}% of the voicemails it is sure of, about two seconds before Twilio’s answering-machine detection would; Twilio still decides every other call.`
              : cutArmed
                ? `It is ending no calls right now, because nothing is being copied — but a cut share of ${saved?.act_voicemail_pct}% is saved, and it takes effect the moment copying starts.`
                : 'It decides nothing — Twilio’s answering-machine detection still makes every call’s decision.'}{' '}
            Changes apply on the next answered call, fleet-wide, with no deploy.
          </p>
        </div>

        {status && <p className="text-muted-foreground text-sm">{status}</p>}
        {error && (
          <div className="border-destructive/40 bg-destructive/5 text-destructive rounded-md border p-3 text-sm">
            {error}
          </div>
        )}

        {saved && effective && (
          <>
            <div
              className={cn(
                'flex items-center justify-between gap-3 rounded-md border p-3',
                live && 'border-emerald-600/40 bg-emerald-600/5',
              )}
            >
              <span className="flex items-center gap-2 text-sm font-medium">
                <span
                  aria-hidden
                  className={cn(
                    'inline-block h-2 w-2 rounded-full',
                    live ? 'bg-emerald-500' : 'bg-muted-foreground/40',
                  )}
                />
                {/* The fleet's own view, not the row: a node that has not taken the
                    broadcast yet is the difference between saved and live. */}
                {live ? `Forking ${effective.sample_pct}% of answered calls` : 'Forking nothing'}
              </span>
              <Button
                variant="outline"
                size="sm"
                className="border-destructive/40 text-destructive hover:bg-destructive/10"
                onClick={() => submit({ enabled: false }, 'fork off')}
                // Enabled whenever anything is still forking, INCLUDING when the row
                // already reads off and a node has not taken the broadcast: re-sending
                // off is how that node is told again. Only a fleet that agrees it is off
                // greys the brake out.
                disabled={saving || (!saved.enabled && !live)}
              >
                Turn off
              </Button>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium" htmlFor="gk-sample-pct">
                Share of calls copied (%)
              </label>
              <input
                id="gk-sample-pct"
                inputMode="numeric"
                value={pct}
                onChange={(e) => setPct(e.target.value)}
                className="border-input bg-background w-full rounded-md border px-3 py-2 text-sm"
              />
              <p className="text-muted-foreground text-xs leading-relaxed">{SAMPLE_HELP}</p>
              {!pctValid && pct.trim() !== '' && (
                <p className="text-destructive text-xs">Whole number between 0 and 100.</p>
              )}
            </div>

            {cutSupported && (
              <div className="flex flex-col gap-1.5 border-t pt-4">
                <div className="flex items-center justify-between gap-3">
                  <label className="text-sm font-medium" htmlFor="gk-cut-pct">
                    Share of sure voicemails it may end (%)
                  </label>
                  <span
                    className={cn(
                      'text-xs font-medium',
                      cutting ? 'text-amber-600' : 'text-muted-foreground',
                    )}
                  >
                    {/* The fleet's own view again: saved-but-not-live matters most for
                        the one dial that ends calls. */}
                    {cutting
                      ? `Ending ${cutLive}% now`
                      : cutArmed
                        ? 'Ending no calls — armed'
                        : 'Ending no calls'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    id="gk-cut-pct"
                    inputMode="numeric"
                    value={cut}
                    onChange={(e) => setCut(e.target.value)}
                    className="border-input bg-background w-full rounded-md border px-3 py-2 text-sm"
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={saveCut}
                    disabled={saving || !cutValid || !cutDirty}
                  >
                    Save
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-destructive/40 text-destructive hover:bg-destructive/10 shrink-0"
                    // One click and no question, like the brake above -- and unlike it,
                    // this one keeps measuring: the fork stays up and only the cutting stops.
                    onClick={() => submit({ act_voicemail_pct: 0 }, 'cut off')}
                    disabled={saving || ((saved.act_voicemail_pct ?? 0) === 0 && cutLive === 0)}
                  >
                    Stop cutting
                  </Button>
                </div>
                <p className="text-muted-foreground text-xs leading-relaxed">{CUT_HELP}</p>
                {!live && (cutLive > 0 || (cutParsed ?? 0) > 0) && (
                  <p className="text-muted-foreground text-xs">
                    No effect while nothing is being copied: a call that is not measured has no
                    verdict to be ended on. It takes effect the moment copying starts.
                  </p>
                )}
                {!cutValid && cut.trim() !== '' && (
                  <p className="text-destructive text-xs">Whole number between 0 and 100.</p>
                )}
              </div>
            )}

            <p className="text-muted-foreground text-xs leading-relaxed">
              The classifier’s other tuning (deadline, pass schedule, word limits) is not editable
              here: the service runs on its own values today, so an edit would be recorded as a
              change that never happened.
              {saved.updated_at && ` Row last changed ${saved.updated_at}.`}
            </p>

            <div className="flex items-center justify-end gap-2">
              <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={save}
                disabled={saving || !pctValid || (saved.enabled ? !pctDirty : parsed === 0)}
              >
                {saving ? 'Saving…' : saved.enabled ? 'Save share' : 'Turn on'}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
